import type { ArrangementDto, ArrangementStatus, AwaitedCommandDto } from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C6a] A COMMAND ROLE STILL AWAITED FROM A DIVISION, IN WORDS — the bell, the printed order and
 *  the order view say it through this file.
 *
 *  ⚠️ ONE SPELLING, THREE SURFACES — AND NOT QUITE ONE RULE FOR WHEN TO SAY IT. `In-charge awaited
 *  from CD` on the bell, `SI A. Roy · awaited from ND` on paper and `Awaited from CD` on the record
 *  page share their words here, because three screens deciding them for themselves is how [C5b §3]
 *  found four of them saying `All Filled` at nobody named. WHEN they say it differs: the bell and the
 *  paper ask the server, which says "awaited" only on a duty whose naming window is still open
 *  (`awaitedCommandsOnDuty`) and never for an archived order; the record page and the preview read
 *  the SEAT (`asks[].seat`), which carries no window, so they say it only while the order is live
 *  (`awaitedIsLive`) and `Not named by CD` after. ⚠️ THE GAP THAT REMAINS: a live order whose naming
 *  window has closed on every duty of the card still reads `Awaited from CD` on the record page while
 *  the bell and the paper say nothing for that role. The exact fix is a server `awaited` flag on
 *  `commandAskSeatSchema`, computed by that same rule.
 *
 *  ⚠️ WORDS AND CODES, NEVER A NUMBER (FR-5.2.6b). `from` is the server's DISTINCT addressee codes
 *  (`awaitedCommandSchema`) — one per addressee still owing a position, never one per empty seat —
 *  and nothing here counts, sums or compares them. Every surface that renders these words runs
 *  `/\b\d+\s*(of|\/|%)/` over what it rendered, with a populated fixture.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ `en-IN`, LIKE DUTY MITRA'S `listOf` (`pwa/src/lib/format.ts`), so the web and the officer app
 * join a list of codes the same way — `CD, ND and CD · TALA`, with no serial comma. Built once.
 */
const LIST = new Intl.ListFormat('en-IN', { type: 'conjunction' });

/** `CD` · `CD and ND` · `CD, ND and CD · TALA` — a list joined the way a sentence joins it. */
export const listOf = (items: readonly string[]): string => LIST.format(items);

/**
 * ⚠️ AN ORDER OVER OR WITHDRAWN OWES NOTHING. `Awaited from CD` is a promise that somebody can still
 * name the officer; on an archived order, or one `CLOSED` or `CANCELLED`, nobody can — the server
 * already says so on paper and on the bell (`awaitedSeatsOnOrder` reads `archived_at: null`, and a
 * closed duty's window is shut). Both hosts of `CommandContacts` ask THIS, so they cannot disagree —
 * and ⚠️ [C6a review] so does the builder's `AskedSummary`, a third reader of `asks[].seat` that said
 * "Awaited from CD" on a CANCELLED order the record page called "Not named by CD".
 */
const ORDER_OVER = ['CLOSED', 'CANCELLED'] as const satisfies readonly ArrangementStatus[];

export const awaitedIsLive = (order: Pick<ArrangementDto, 'archivedAt' | 'status'>): boolean =>
  !order.archivedAt && !(ORDER_OVER as readonly ArrangementStatus[]).includes(order.status);

/** `awaited from CD and ND` — after a role's names on paper, or on its own where nobody is named. */
export const awaitedFrom = (from: readonly string[]): string => `awaited from ${listOf(from)}`;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ THE BELL'S PHRASE — `In-charge awaited from CD`, and `null` when nothing is awaited.
 *
 *  Roles owed by the SAME addressees share one clause (`In-charge and Assisting Inspector awaited
 *  from CD`), in the order the server listed them (card order, then role order); different
 *  addressees are separate clauses joined by `;`. Two identical clauses side by side would be a
 *  count spelled as repetition, which is exactly what a command role may not become.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const awaitedRolesPhrase = (awaited: readonly AwaitedCommandDto[]): string | null => {
  const sameCodes = (left: readonly string[], right: readonly string[]): boolean =>
    left.every((code, at) => right[at] === code) && right.every((code, at) => left[at] === code);

  const clauses: { from: readonly string[]; labels: string[] }[] = [];
  for (const role of awaited) {
    const same = clauses.find((clause) => sameCodes(clause.from, role.from));
    if (!same) clauses.push({ from: role.from, labels: [role.label] });
    else if (!same.labels.includes(role.label)) same.labels.push(role.label);
  }
  return clauses[0] === undefined
    ? null
    : clauses.map((clause) => `${listOf(clause.labels)} ${awaitedFrom(clause.from)}`).join('; ');
};
